import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, RefreshCw, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import api, { fmtARS, apiError } from "@/lib/api";

const empty = { name: "", unit: "barra 6m", price: 0, stock_length_mm: 6000, category: "Hierro" };
const UNITS = ["barra 6m", "barra 12m", "unidad", "kg", "m2", "metro", "litro"];

export default function Materials() {
  const [materials, setMaterials] = useState([]);
  const [editing, setEditing] = useState(null);
  const [editId, setEditId] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncInfo, setSyncInfo] = useState(null);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");

  const load = () => api.get("/materials").then((r) => setMaterials(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const categories = [...new Set(materials.map((m) => m.category).filter(Boolean))].sort();
  const words = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const filtered = materials.filter((m) => {
    if (category && m.category !== category) return false;
    if (!words.length) return true;
    const haystack = [m.name, m.category, m.source_code].filter(Boolean).join(" ").toLowerCase();
    return words.every((w) => haystack.includes(w));
  });

  const save = async () => {
    if (!editing.name) { toast.error("Ingresá el nombre del material"); return; }
    const payload = { ...editing, price: Number(editing.price || 0), stock_length_mm: editing.stock_length_mm ? Number(editing.stock_length_mm) : null };
    try {
      if (editId) await api.put(`/materials/${editId}`, payload);
      else await api.post("/materials", payload);
      toast.success("Material guardado");
      setEditing(null); setEditId(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminar este material definitivamente?")) return;
    try { await api.delete(`/materials/${id}`); load(); toast.success("Material eliminado"); } catch (e) { toast.error(apiError(e)); }
  };

  const syncTrotta = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/materials/sources/trotta/sync");
      setSyncInfo(data);
      toast.success(`Trotta actualizado: ${data.count} precios`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div data-testid="materials-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <h1 className="font-heading font-bold uppercase text-2xl tracking-tight">Materiales</h1>
        <div className="flex flex-col sm:flex-row gap-2">
          <Button onClick={syncTrotta} disabled={syncing} variant="outline" className="border-neutral-700 rounded-sm font-heading uppercase text-xs hover:bg-neutral-800 hover:text-white" data-testid="sync-trotta-btn">
            <RefreshCw className={`w-4 h-4 mr-1 ${syncing ? "animate-spin" : ""}`} /> Actualizar Trotta
          </Button>
          <Button onClick={() => { setEditing({ ...empty }); setEditId(null); }} className="bg-[#F2B90D] hover:bg-[#DFA90C] text-black rounded-sm font-heading uppercase text-xs" data-testid="new-material-btn">
            <Plus className="w-4 h-4 mr-1" /> Nuevo material
          </Button>
        </div>
      </div>

      {syncInfo && (
        <div className="mb-4 bg-[#171717] border border-neutral-800 rounded-md p-3 text-sm text-neutral-300 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <span>Hierros Trotta: {syncInfo.count} precios sincronizados en {syncInfo.groups?.length || 1} rubros. Nuevos {syncInfo.created}, actualizados {syncInfo.updated}.</span>
          <a href={syncInfo.url} target="_blank" rel="noreferrer" className="text-[#F2B90D] inline-flex items-center gap-1">
            Ver fuente <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      )}

      {materials.length === 0 && <p className="text-neutral-500 text-sm">Cargá tus materiales (hierro, caños, electrodos...) con su precio para usarlos en las cotizaciones.</p>}

      {materials.length > 0 && (
        <div className="flex flex-col sm:flex-row gap-2 mb-4">
          <Input placeholder="Buscar material por nombre, código o rubro..." value={search} onChange={(e) => setSearch(e.target.value)} className="bg-neutral-900 border-neutral-800 rounded-sm" data-testid="material-search-input" />
          <select value={category} onChange={(e) => setCategory(e.target.value)} className="h-10 bg-neutral-900 border border-neutral-800 rounded-sm px-3 text-sm sm:w-64" data-testid="material-category-filter">
            <option value="">Todos los rubros ({materials.length})</option>
            {categories.map((c) => <option key={c} value={c}>{c} ({materials.filter((m) => m.category === c).length})</option>)}
          </select>
        </div>
      )}

      {materials.length > 0 && filtered.length === 0 && <p className="text-neutral-500 text-sm">No se encontraron materiales con ese filtro.</p>}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {filtered.map((m) => (
          <div key={m.id} className="bg-[#171717] border border-neutral-800 rounded-md p-5 hover:border-neutral-700 transition-colors" data-testid={`material-card-${m.id}`}>
            <div className="flex items-start justify-between gap-2 mb-3">
              <div className="min-w-0">
                <p className="text-sm font-medium break-words" title={m.name}>{m.name}</p>
                {m.category && <p className="text-[11px] text-neutral-500 mt-0.5">{m.category}</p>}
                {m.source === "hierros_trotta" && <p className="text-[10px] text-[#F2B90D] mt-1">Trotta #{m.source_code}</p>}
              </div>
              <div className="flex gap-1 shrink-0">
                <Button size="sm" variant="outline" onClick={() => { setEditing({ name: m.name, unit: m.unit, price: m.price, stock_length_mm: m.stock_length_mm, category: m.category || "", source: m.source || null, source_group: m.source_group || null, source_code: m.source_code || null, source_url: m.source_url || null, base_price: m.base_price || null, synced_at: m.synced_at || null }); setEditId(m.id); }}
                  className="border-neutral-700 rounded-sm h-7 w-7 p-0 hover:bg-neutral-800 hover:text-white" data-testid={`material-edit-${m.id}`}>
                  <Pencil className="w-3 h-3" />
                </Button>
                <Button size="sm" variant="outline" onClick={() => remove(m.id)} className="border-neutral-700 rounded-sm h-7 w-7 p-0 text-red-400 hover:bg-red-500/10 hover:text-red-400" data-testid={`material-delete-${m.id}`}>
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            </div>
            <div className="flex items-end justify-between pt-3 border-t border-neutral-800">
              <span className="text-[11px] uppercase text-neutral-500">{m.unit}</span>
              <span className="font-mono text-base font-bold text-[#F2B90D]">{fmtARS(m.price)}</span>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-[#171717] border-neutral-800 text-white">
          <DialogHeader><DialogTitle className="font-heading uppercase">{editId ? "Editar material" : "Nuevo material"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <Input placeholder='Nombre (ej: Hierro ángulo 1")' value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="bg-neutral-900 border-neutral-800 rounded-sm" data-testid="material-name-input" />
              <div className="grid grid-cols-2 gap-3">
                <Input placeholder="Categoría" value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} className="bg-neutral-900 border-neutral-800 rounded-sm" data-testid="material-category-input" />
                <select value={editing.unit} onChange={(e) => setEditing({ ...editing, unit: e.target.value })} className="h-10 bg-neutral-900 border border-neutral-800 rounded-sm px-3 text-sm" data-testid="material-unit-select">
                  {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-neutral-400 block mb-1">Precio ($)</label>
                  <Input type="number" value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} className="bg-neutral-900 border-neutral-800 rounded-sm" data-testid="material-price-input" />
                </div>
                <div>
                  <label className="text-xs text-neutral-400 block mb-1">Largo de barra (mm, opcional)</label>
                  <Input type="number" value={editing.stock_length_mm || ""} onChange={(e) => setEditing({ ...editing, stock_length_mm: e.target.value })} className="bg-neutral-900 border-neutral-800 rounded-sm" data-testid="material-length-input" />
                </div>
              </div>
              <Button onClick={save} className="w-full bg-[#F2B90D] hover:bg-[#DFA90C] text-black rounded-sm font-heading uppercase" data-testid="material-save-btn">Guardar</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
